import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaPlane } from "react-icons/fa";
import "../css/styles.css";

const LuxuryFlyingSafari = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348685/8_Day_Best_of_Kenya_Luxury_Flying_Safari_Diani_Beach_2_g1mr45.jpg"
            alt="Luxury Flying Safari Kenya"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Luxury Safari</span>
          <h1>8 Day Best of Kenya Luxury Flying Safari + Diani Beach</h1>
          <p className="safari-detail-hero-sub">
            Fly between iconic wildlife destinations in private light aircraft, stay in luxury lodges, and unwind on the turquoise shores of Diani Beach.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Luxury Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>Luxury Safari &amp; Beach Escape</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            Experience the ultimate Kenyan safari in unmatched comfort and style. This journey combines private light aircraft transfers between iconic wildlife destinations with stays in luxury lodges and camps, giving you more time to relax, explore, and soak in Africa's extraordinary landscapes.
          </p>
          <p>
            Begin in the legendary Masai Mara, fly to Amboseli, and conclude with four nights on Diani Beach, where the turquoise Indian Ocean meets pristine white sands. Every moment is designed for travelers who value comfort, privacy, and unforgettable experiences.
          </p>
          <p>
            This is more than a safari — it's a luxury journey through Kenya's most breathtaking wilderness and coastlines.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Fly in style between Nairobi, Masai Mara, and Amboseli — saving time for exploration and relaxation</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Stay in luxury lodges with stunning locations and exceptional service</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Search for the Big Five across Kenya's top wildlife areas</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Four nights of relaxation at Diani Beach in an exclusive resort</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Optional sunrise hot air balloon safari over the Masai Mara plains</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Private game drives for a more intimate wildlife experience</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>8 Days / 7 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaPlane className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Masai Mara &ndash; Amboseli &ndash; Diani Beach</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Arrive in Nairobi and meet your Nyatwende representative. Transfer to your luxury hotel in the city, relax after your journey, or explore Nairobi's top attractions such as the Giraffe Centre or David Sheldrick Elephant Orphanage.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Nairobi. <br />
                <strong>Accommodation:</strong> Hemingways Nairobi / Movenpick Residences
              </p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Masai Mara <span className="safari-detail-flight-badge"><FaPlane /> Flight</span></h3>
              <p>
                After breakfast, board your private light aircraft to the Masai Mara. Arrive in time for lunch at your luxury lodge, then enjoy your first afternoon game drive across the iconic savannah.
              </p>
              <p>
                The Mara's golden plains stretch endlessly, home to lions, elephants, giraffes, and the full spectrum of Africa's wildlife.
              </p>
              <p className="safari-detail-day-note">
                Overnight in the Masai Mara. <br />
                <strong>Accommodation:</strong> Angama Mara / Mahali Mzuri / Mara Serena Lodge
              </p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Masai Mara</h3>
              <p>
                Spend a full day discovering the extraordinary wildlife of the Mara. Morning and afternoon game drives offer the best opportunities to see the Big Five and capture stunning photographs.
              </p>
              <p>
                Optional experiences include a sunrise hot air balloon safari, where you'll drift silently above the plains as the wildlife awakens below. Relax in the evenings at your lodge, enjoying gourmet meals and luxurious views.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Masai Mara to Amboseli <span className="safari-detail-flight-badge"><FaPlane /> Flight</span></h3>
              <p>
                After breakfast, board your light aircraft for a scenic flight to Amboseli National Park, famed for its majestic elephant herds and views of Mount Kilimanjaro.
              </p>
              <p>
                Arrive in time for lunch at your lodge, then enjoy an afternoon game drive in this iconic park. The backdrop of Kilimanjaro makes every photo and memory spectacular.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Amboseli. <br />
                <strong>Accommodation:</strong> Ol Tukai Lodge / Amboseli Serena Safari Lodge
              </p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Full Day in Amboseli</h3>
              <p>
                Spend the day exploring Amboseli's rich wildlife and stunning landscapes. Morning and afternoon game drives allow you to see elephants, lions, giraffes, zebras, and countless birds.
              </p>
              <p>
                Enjoy sundowners on the plains as the sun sets behind Mount Kilimanjaro — an unforgettable experience in Kenya's wilderness.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Amboseli to Diani Beach <span className="safari-detail-flight-badge"><FaPlane /> Flight</span></h3>
              <p>
                After breakfast, transfer to Mombasa Airport via a private flight or charter. Arrive at your luxury beach resort in Diani, where turquoise waters and soft white sands await.
              </p>
              <p>
                Check in and enjoy the afternoon at leisure — swim, sunbathe, or sip a cocktail as the Indian Ocean breeze surrounds you.
              </p>
              <p className="safari-detail-day-note">
                Overnight in Diani Beach. <br />
                <strong>Accommodation:</strong> Almanara Luxury Villas / Baobab Beach Resort
              </p>
            </div>
          </div>

          {/* Days 7–9 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7‑9</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 7–9 — Diani Beach Relaxation</h3>
              <p>
                Spend three full days enjoying your luxury coastal escape.
              </p>
              <p><strong>Optional activities include:</strong></p>
              <ul className="safari-detail-day-list">
                <li>Snorkeling or diving in Kisite-Mpunguti Marine Park</li>
                <li>Private dhow sunset cruises along the coast</li>
                <li>Spa treatments and wellness experiences at your resort</li>
                <li>Exploring local markets and culture</li>
              </ul>
              <p>
                These days are all about rest, relaxation, and savoring the Kenyan coast in style.
              </p>
              <p className="safari-detail-day-note">Overnight in Diani Beach.</p>
            </div>
          </div>

          {/* Day 10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">10</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 10 — Diani Beach to Nairobi / Departure</h3>
              <p>
                After breakfast, transfer to Mombasa Airport for a domestic flight back to Nairobi. Connect with your international departure flight or stay overnight in Nairobi if required.
              </p>
              <p className="safari-detail-day-note">
                Your luxury safari ends here, leaving you with memories of wildlife, comfort, and the Indian Ocean that will stay with you forever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Hemingways Nairobi</li>
            </ul>
          </div>
        </div>

        {/* Masai Mara */}
        <h3 className="safari-detail-accom-region-title">Masai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Angama Mara</li>
              <li><FaHotel /> Mahali Mzuri</li>
            </ul>
          </div>
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
              <li><FaHotel /> Amboseli Serena Safari Lodge (luxury tented suites)</li>
            </ul>
          </div>
        </div>

        {/* Diani Beach */}
        <h3 className="safari-detail-accom-region-title">Diani Beach</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Almanara Luxury Villas</li>
              <li><FaHotel /> Baobab Beach Resort</li>
              <li><FaHotel /> Leopard Beach Resort</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          All lodges are carefully selected for luxury, comfort, and unforgettable experiences. Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Nairobi</li>
              <li>Private light aircraft transfers between Nairobi, Masai Mara, and Amboseli</li>
              <li>Transport in a luxury safari vehicle with professional guide during game drives</li>
              <li>Park entrance fees</li>
              <li>Luxury accommodation during safari and beach stay</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari, dhow cruises, or spa treatments</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This luxury safari can be tailored to your desires: extend your stay on the Kenyan coast, upgrade to private villas or exclusive camps, or add unique experiences like private bush dinners or heli-safaris.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=8 Day Best of Kenya Luxury Flying Safari Diani Beach" className="hero-cta primary">
              Plan Your Luxury Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LuxuryFlyingSafari;
